import { type ReactNode } from "react";
import { roleAtLeast, type RoleState } from "../../state/identity.tsx";

/**
 * The role-gating primitives for the Alerts screen's WRITE controls (design 12.3 / FR-12 AC6:
 * "controls the role cannot use are hidden or disabled with a reason, and the server enforces
 * regardless"). Every ack / rule / silence / channel write button goes through {@link WriteButton},
 * and every write FORM through {@link canWrite} + {@link writeDisabledReason}, so the rule is applied
 * identically everywhere and the reason is always legible.
 *
 * The policy, matching the contract (`x-required-role: operator` on all writes):
 * - role at least `operator` → the control is ENABLED.
 * - a known role below operator (`viewer`) → the control is DISABLED, with a reason that says the
 *   operator role is required.
 * - `unknown` role → DISABLED under least privilege, with a reason that says the role is not resolved
 *   yet; the server enforces regardless.
 *
 * The control is DISABLED-with-a-reason rather than hidden, so a viewer can see the capability exists
 * and why it is unavailable (more honest than a blank space). The server is still the authority: even
 * if the button were forced, the API returns 403 and the screen surfaces it.
 */

/** True only when the role is known to be at least operator (writes are operator-level). */
export function canWrite(role: RoleState): boolean {
  return roleAtLeast(role, "operator");
}

/** The reason a write is unavailable for a role, or null when the role may write. */
export function writeDisabledReason(role: RoleState): string | null {
  if (canWrite(role)) return null;
  if (role === "unknown") {
    return "Your role has not been resolved yet, so write actions are disabled. The server enforces access regardless.";
  }
  return "Write actions need the operator role. The server enforces access regardless.";
}

export interface WriteButtonProps {
  readonly role: RoleState;
  readonly onClick: () => void;
  readonly children: ReactNode;
  /** An extra disabled condition (e.g. a pending mutation or an invalid form). */
  readonly disabled?: boolean;
  /** Button type; defaults to `button`. */
  readonly type?: "button" | "submit";
  /** `primary` for the main action, `danger` for destructive, `ghost` for secondary. */
  readonly variant?: "primary" | "danger" | "ghost";
  readonly title?: string;
}

/**
 * A write button that disables itself (with an explanatory `title` and `aria-describedby`-style
 * note) when the role cannot write, OR when the caller passes an extra disabled condition. When
 * disabled for the role, the reason is announced to assistive tech via `title` and a visually-hidden
 * note, so the "disabled with a reason" rule is met for keyboard and screen-reader users too.
 */
export function WriteButton(props: WriteButtonProps): ReactNode {
  const roleReason = writeDisabledReason(props.role);
  const disabledForRole = roleReason !== null;
  const disabled = disabledForRole || props.disabled === true;
  const title = disabledForRole ? roleReason : props.title;

  return (
    <button
      type={props.type ?? "button"}
      className="alr-btn"
      data-variant={props.variant ?? "ghost"}
      onClick={props.onClick}
      disabled={disabled}
      title={title ?? undefined}
      aria-disabled={disabled}
    >
      {props.children}
      {disabledForRole && <span className="sr-only">: {roleReason}</span>}
    </button>
  );
}

/**
 * A small inline note explaining that writes are unavailable for the role, shown ONCE at the top of
 * a region whose write controls are all disabled, so a viewer understands the whole region is
 * read-only without reading each disabled button's tooltip.
 */
export function ReadOnlyNote(props: { role: RoleState }): ReactNode {
  const reason = writeDisabledReason(props.role);
  if (reason === null) return null;
  return (
    <p className="alr-readonly-note" role="note">
      {reason}
    </p>
  );
}
